import React, { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { parseSunfireCsvFull, SunfireRowFull } from '../services/csvParser';
import { buildMatchPlan, buildExportItems, SmsMatchedPair, RecordingRecord, MultiCallGroup } from '../services/smsExportService';
import MultiCallModal from '../components/MultiCallModal';

type Step = 'csv' | 'match' | 'export';

export default function ExportSms() {
  const [step, setStep] = useState<Step>('csv');
  const [csvRows, setCsvRows] = useState<SunfireRowFull[]>([]);
  const [csvFilename, setCsvFilename] = useState('');
  // Default to all systems: a requested Medicare call can live in any system
  // (DialedIn, LeadMomentum, etc.), and the operator shouldn't have to know
  // which. The dropdown still lets them narrow to one system if desired.
  const [systemId, setSystemId] = useState('all');
  const [matchedPairs, setMatchedPairs] = useState<SmsMatchedPair[]>([]);
  const [unmatchedCsv, setUnmatchedCsv] = useState<SunfireRowFull[]>([]);
  const [unmatchedRecs, setUnmatchedRecs] = useState<RecordingRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<any>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [manualCsvIdx, setManualCsvIdx] = useState<number | null>(null);
  // True when the /search response hit its result cap — some recordings in
  // the CSV's date range may be missing from the match pool.
  const [searchCapped, setSearchCapped] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Set on unmount so the export polling loop stops and no setState fires
  // after the user navigates away.
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => { cancelledRef.current = true; };
  }, []);

  // Multi-call groups: a CSV row that matched >1 recording. Resolved by the
  // modal, which records which recording s3Keys to send per confirmation number.
  const [groups, setGroups] = useState<MultiCallGroup[]>([]);
  const [groupSelections, setGroupSelections] = useState<Record<string, string[]>>({});
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);

  const groupKey = (g: MultiCallGroup) => g.csvRow.confirmationNumber || g.csvRow.applicantPhone;
  const isResolved = (g: MultiCallGroup) => (groupSelections[groupKey(g)]?.length ?? 0) > 0;
  const unresolvedGroups = groups.filter((g) => !isResolved(g));

  // Pairs derived from resolved groups (selected candidates become matched pairs).
  const groupPairs = (): SmsMatchedPair[] =>
    groups.flatMap((g) => {
      const picked = new Set(groupSelections[groupKey(g)] || []);
      return g.candidates
        .filter((c) => picked.has(c.s3Key))
        .map((c) => ({ csvRow: g.csvRow, recording: c, matchType: 'auto' as const }));
    });

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  // ==================== CSV LOADING ====================
  const handleCsvFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rows = parseSunfireCsvFull(e.target?.result as string);
        if (rows.length === 0) {
          showMessage('No valid rows found in CSV', 'error');
          return;
        }
        setCsvRows(rows);
        setCsvFilename(file.name);
        showMessage(`Loaded ${rows.length} rows from ${file.name}`, 'success');
      } catch (err: any) {
        showMessage('Failed to parse CSV: ' + err.message, 'error');
      }
    };
    reader.readAsText(file);
  };

  const handleCsvDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.type === 'text/csv')) handleCsvFile(file);
  };

  const handleCsvInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleCsvFile(file);
  };

  // ==================== MATCHING ====================
  const fetchAndMatch = async () => {
    setLoading(true);
    try {
      const dates = csvRows.map(r => r.date).filter(Boolean).sort();
      const dateFrom = dates[0] || '';
      const dateTo = dates[dates.length - 1] || '';
      if (!dateFrom) {
        showMessage('No valid dates in CSV — cannot search for recordings', 'error');
        setLoading(false);
        return;
      }

      const result = await api.search({
        systemId: systemId === 'all' ? undefined : systemId,
        dateFrom,
        dateTo,
      });
      setSearchCapped(result.capped === true);

      const recs: RecordingRecord[] = (result.recordings || []).map((r: any) => ({
        PK: r.PK,
        SK: r.SK,
        s3Key: r.s3Key,
        originalFilename: r.originalFilename || r.normalizedFilename || '',
        recordDate: r.recordDate,
        agentId: r.agentId,
        agentEmail: r.agentEmail,
        duration: r.duration || 0,
        systemId: r.systemId,
        callerId: r.callerId,
        direction: r.direction,
        campaign: r.campaign,
        callTimestamp: r.callTimestamp,
        archivePath: r.archivePath,
      }));

      const plan = buildMatchPlan(csvRows, recs);
      setMatchedPairs(plan.auto);
      setUnmatchedCsv(plan.unmatchedCsv);
      setUnmatchedRecs(plan.unmatchedRecordings);
      setGroups(plan.groups);
      setGroupSelections({});
      // Auto-pop the first unresolved group, if any.
      setActiveGroupKey(plan.groups.length > 0 ? (plan.groups[0].csvRow.confirmationNumber || plan.groups[0].csvRow.applicantPhone) : null);

      if (plan.auto.length === 0 && plan.groups.length === 0) {
        showMessage(`Found ${recs.length} recordings but no matches. Check system filter and date range.`, 'error');
      } else {
        const g = plan.groups.length ? ` · ${plan.groups.length} need multi-call review` : '';
        showMessage(`Matched ${plan.auto.length} of ${csvRows.length} CSV rows${g}`, 'success');
      }
      setStep('match');
    } catch (err: any) {
      showMessage('Failed to search recordings: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Manual match: pair a selected CSV row with a clicked recording
  const handleManualMatch = (recIdx: number) => {
    if (manualCsvIdx === null) return;
    const csvRow = unmatchedCsv[manualCsvIdx];
    const recording = unmatchedRecs[recIdx];
    setMatchedPairs(prev => [...prev, { csvRow, recording, matchType: 'manual' }]);
    setUnmatchedCsv(prev => prev.filter((_, i) => i !== manualCsvIdx));
    setUnmatchedRecs(prev => prev.filter((_, i) => i !== recIdx));
    setManualCsvIdx(null);
  };

  const handleUnmatch = (idx: number) => {
    const pair = matchedPairs[idx];
    setUnmatchedCsv(prev => [...prev, pair.csvRow]);
    setUnmatchedRecs(prev => [...prev, pair.recording]);
    setMatchedPairs(prev => prev.filter((_, i) => i !== idx));
  };

  // ==================== MULTI-CALL GROUPS ====================
  const activeGroup = groups.find((g) => groupKey(g) === activeGroupKey) || null;

  const advanceToNextUnresolved = () => {
    const next = groups.find((g) => !isResolved(g) && groupKey(g) !== activeGroupKey);
    setActiveGroupKey(next ? groupKey(next) : null);
  };

  const handleGroupConfirm = (selectedS3Keys: string[]) => {
    if (!activeGroup) return;
    setGroupSelections((prev) => ({ ...prev, [groupKey(activeGroup)]: selectedS3Keys }));
    advanceToNextUnresolved();
  };

  const handleGroupRemove = () => {
    if (!activeGroup) return;
    // Drop the enrollment from the batch: move its CSV row to unmatched, remove the group.
    setUnmatchedCsv((prev) => [...prev, activeGroup.csvRow]);
    setUnmatchedRecs((prev) => [...prev, ...activeGroup.candidates]);
    setGroups((prev) => prev.filter((g) => groupKey(g) !== groupKey(activeGroup)));
    advanceToNextUnresolved();
  };

  // ==================== EXPORT ====================
  const handleExport = async (dryRun: boolean = false) => {
    setExporting(true);
    setExportResult(null);
    try {
      const items = buildExportItems([...matchedPairs, ...groupPairs()]);
      const result = await api.startSmsExport(items, dryRun);

      if (result.jobId && result.status === 'processing') {
        // Async — poll for status
        let attempts = 0;
        while (attempts < 60) {
          await new Promise(r => setTimeout(r, 3000));
          if (cancelledRef.current) return; // navigated away — stop polling
          const status = await api.checkSmsExport(result.jobId);
          if (cancelledRef.current) return;
          if (status.status === 'complete' || status.status === 'dry_run') {
            setExportResult(status);
            break;
          }
          attempts++;
        }
      } else {
        if (cancelledRef.current) return;
        setExportResult(result);
      }
    } catch (err: any) {
      if (cancelledRef.current) return;
      showMessage('Export failed: ' + err.message, 'error');
    } finally {
      if (!cancelledRef.current) setExporting(false);
    }
  };

  const extractPhone = (filename: string) => {
    // Phone position varies by system (DialedIn prefixes list id/campaign/agent
    // email before the phone), so scan for the 10-digit field — matches the
    // service's extractPhoneFromRecording.
    for (const field of (filename || '').split('_')) {
      if (field.includes('@')) continue;
      const digits = field.replace(/\D/g, '');
      if (digits.length === 10 || (digits.length === 11 && digits[0] === '1')) {
        return digits.slice(-10);
      }
    }
    return '';
  };

  return (
    <div>
      <div className="page-header">
        <h1>Export to SMS</h1>
        <p>Match Sunfire CSV data to existing recordings and export to ConverselyAI for Medicare compliance review.</p>
      </div>

      {message && (
        <div style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 6,
          background: message.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: message.type === 'success' ? '#2e7d32' : '#c62828', fontSize: 13 }}>
          {message.text}
        </div>
      )}

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
        {(['csv', 'match', 'export'] as Step[]).map((s, i) => (
          <div key={s} style={{
            flex: 1, padding: '8px 0', textAlign: 'center', fontSize: 12, fontWeight: 600,
            background: step === s ? '#00bcd4' : i < ['csv', 'match', 'export'].indexOf(step) ? '#e0f7fa' : '#f5f5f5',
            color: step === s ? '#fff' : '#666', borderRadius: i === 0 ? '8px 0 0 8px' : i === 2 ? '0 8px 8px 0' : 0,
          }}>
            {i + 1}. {s === 'csv' ? 'Load CSV' : s === 'match' ? 'Match Recordings' : 'Export'}
          </div>
        ))}
      </div>

      {/* ==================== STEP 1: CSV ==================== */}
      {step === 'csv' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
          <h3 style={{ margin: '0 0 16px 0', color: 'var(--text-heading)' }}>Load Sunfire CSV</h3>

          <div
            onDrop={handleCsvDrop}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#00bcd4'; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = '#ccc'; }}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed #ccc', borderRadius: 12, padding: '36px 24px', textAlign: 'center',
              cursor: 'pointer', transition: 'border-color 0.2s', marginBottom: 16,
            }}
          >
            <div style={{ fontSize: 15, color: '#555', marginBottom: 4 }}>
              {csvFilename ? `Loaded: ${csvFilename} (${csvRows.length} rows)` : 'Drag and drop Sunfire CSV here, or click to browse'}
            </div>
            <div style={{ fontSize: 12, color: '#999' }}>CSV file with enrollment data — all fields preserved for ConverselyAI</div>
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvInput} style={{ display: 'none' }} />
          </div>

          {csvRows.length > 0 && (
            <>
              <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e8e8e8', borderRadius: 8, marginBottom: 16 }}>
                <table className="data-table" style={{ fontSize: 11, marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>Phone</th>
                      <th>Name</th>
                      <th>Date</th>
                      <th>Agent</th>
                      <th>Carrier</th>
                      <th>Plan</th>
                      <th>Confirmation #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvRows.map((row, i) => (
                      <tr key={i} style={{ background: (!row.applicantPhone || row.applicantPhone.replace(/\D/g, '').length < 7) ? '#fff3e0' : undefined }}>
                        <td style={{ fontFamily: 'monospace', padding: 0 }}>
                          <input
                            type="text"
                            value={row.applicantPhone || ''}
                            onChange={e => {
                              const updated = [...csvRows];
                              updated[i] = { ...updated[i], applicantPhone: e.target.value };
                              setCsvRows(updated);
                            }}
                            style={{ width: '100%', padding: '6px 8px', border: 'none', borderBottom: '1px solid #e0e0e0', fontFamily: 'monospace', fontSize: 12, background: 'transparent' }}
                            placeholder="Enter phone #"
                          />
                        </td>
                        <td>{row.applicantFirstName} {row.applicantLastName}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{row.submitDate}</td>
                        <td style={{ fontSize: 10 }}>{row.agentName}</td>
                        <td style={{ fontSize: 10 }}>{row.rawFields?.CarrierName || '-'}</td>
                        <td style={{ fontSize: 10 }}>{row.rawFields?.PlanName || '-'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 10 }}>{row.confirmationNumber}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select value={systemId} onChange={e => setSystemId(e.target.value)}
                  style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
                  <option value="all">All Systems</option>
                  <option value="dialedin">DialedIn</option>
                  <option value="pulsedial">PulseDial</option>
                  <option value="vicidial">Vicidial</option>
                  <option value="leadmomentum">LeadMomentum</option>
                </select>
                <button onClick={fetchAndMatch} disabled={loading}
                  style={{ padding: '8px 20px', background: '#00bcd4', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  {loading ? 'Searching...' : 'Find & Match Recordings'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ==================== STEP 2: MATCH ==================== */}
      {step === 'match' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-heading)' }}>Match Results</h3>
          {searchCapped && (
            <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 6, background: '#fff3e0', border: '1px solid #ffb74d', color: '#e65100', fontSize: 13 }}>
              Search hit the result cap — some calls in this range may be missing. Narrow the date range.
            </div>
          )}
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#888' }}>
            <strong style={{ color: (matchedPairs.length + groups.length) === csvRows.length ? '#2e7d32' : 'var(--text-heading)' }}>
              {matchedPairs.length + groups.length} / {csvRows.length}
            </strong> CSV rows matched to recordings.
            {unmatchedCsv.length > 0 && <span style={{ color: '#e65100' }}> {unmatchedCsv.length} unmatched.</span>}
          </p>

          {/* Matched pairs */}
          {matchedPairs.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#2e7d32' }}>Matched ({matchedPairs.length})</h4>
              <div style={{ maxHeight: 250, overflowY: 'auto', border: '1px solid #e8e8e8', borderRadius: 8 }}>
                <table className="data-table" style={{ fontSize: 11, marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>Phone</th>
                      <th>Name</th>
                      <th>Date</th>
                      <th>Agent</th>
                      <th>Recording</th>
                      <th>Match</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchedPairs.map((pair, i) => (
                      <tr key={i} style={{ background: '#f1f8e9' }}>
                        <td style={{ fontFamily: 'monospace' }}>{pair.csvRow.applicantPhone}</td>
                        <td>{pair.csvRow.applicantFirstName} {pair.csvRow.applicantLastName}</td>
                        <td>{pair.csvRow.date}</td>
                        <td style={{ fontSize: 10 }}>{pair.csvRow.agentName}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 10 }}>{pair.recording.originalFilename.slice(0, 40)}...</td>
                        <td><span style={{ fontSize: 10, color: pair.matchType === 'auto' ? '#2e7d32' : '#1565c0' }}>{pair.matchType}</span></td>
                        <td>
                          <button onClick={() => handleUnmatch(i)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 11 }}>unmatch</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Multi-call groups */}
          {groups.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#e65100' }}>
                Multiple calls detected ({groups.length}) — resolve each before exporting
              </h4>
              <div style={{ border: '1px solid #e8e8e8', borderRadius: 8 }}>
                {groups.map((g) => {
                  const sel = groupSelections[groupKey(g)] || [];
                  return (
                    <div key={groupKey(g)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
                      <span>
                        {g.csvRow.applicantFirstName} {g.csvRow.applicantLastName} · {g.candidates.length} calls ·{' '}
                        {sel.length > 0
                          ? <span style={{ color: '#2e7d32' }}>{sel.length} selected</span>
                          : <span style={{ color: '#e65100' }}>unresolved</span>}
                      </span>
                      <button type="button" onClick={() => setActiveGroupKey(groupKey(g))}
                        style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                        {sel.length > 0 ? `Edit (${g.candidates.length})` : `Resolve (${g.candidates.length})`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Unmatched CSV rows */}
          {unmatchedCsv.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#e65100' }}>Unmatched CSV Rows ({unmatchedCsv.length}) — click to select, then click a recording below</h4>
              <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e8e8e8', borderRadius: 8 }}>
                <table className="data-table" style={{ fontSize: 11, marginBottom: 0 }}>
                  <thead><tr><th>Phone</th><th>Name</th><th>Date</th><th>Agent</th></tr></thead>
                  <tbody>
                    {unmatchedCsv.map((row, i) => (
                      <tr key={i} onClick={() => setManualCsvIdx(manualCsvIdx === i ? null : i)}
                        style={{ cursor: 'pointer', background: manualCsvIdx === i ? '#fff3e0' : undefined }}>
                        <td style={{ fontFamily: 'monospace' }}>{row.applicantPhone}</td>
                        <td>{row.applicantFirstName} {row.applicantLastName}</td>
                        <td>{row.date}</td>
                        <td style={{ fontSize: 10 }}>{row.agentName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Unmatched recordings */}
          {unmatchedRecs.length > 0 && manualCsvIdx !== null && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#1565c0' }}>
                Available Recordings ({unmatchedRecs.length}) — click to match with selected CSV row
              </h4>
              <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e8e8e8', borderRadius: 8 }}>
                <table className="data-table" style={{ fontSize: 11, marginBottom: 0 }}>
                  <thead><tr><th>Phone</th><th>Date</th><th>Agent</th><th>Filename</th></tr></thead>
                  <tbody>
                    {unmatchedRecs.map((rec, i) => (
                      <tr key={i} onClick={() => handleManualMatch(i)} style={{ cursor: 'pointer' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#e3f2fd'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                        <td style={{ fontFamily: 'monospace' }}>{extractPhone(rec.originalFilename)}</td>
                        <td>{rec.recordDate}</td>
                        <td>{rec.agentId}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 10 }}>{rec.originalFilename.slice(0, 50)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
            <button onClick={() => setStep('csv')}
              style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
              Back
            </button>
            <button onClick={() => setStep('export')} disabled={matchedPairs.length + groupPairs().length === 0 || unresolvedGroups.length > 0}
              style={{
                padding: '8px 20px', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                background: (matchedPairs.length + groupPairs().length > 0 && unresolvedGroups.length === 0) ? '#00bcd4' : '#ccc', color: '#fff',
              }}>
              {unresolvedGroups.length > 0 ? `Resolve ${unresolvedGroups.length} multi-call group(s)` : `Continue to Export (${matchedPairs.length + groupPairs().length})`}
            </button>
          </div>
        </div>
      )}

      {/* ==================== STEP 3: EXPORT ==================== */}
      {step === 'export' && (() => {
        const exportPairs = [...matchedPairs, ...groupPairs()];
        return (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-heading)' }}>Export to ConverselyAI</h3>
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#888' }}>
            Ready to send <strong>{exportPairs.length}</strong> recording{exportPairs.length !== 1 ? 's' : ''} with metadata to SMS for compliance review.
          </p>

          {/* Summary table */}
          <div style={{ maxHeight: 250, overflowY: 'auto', border: '1px solid #e8e8e8', borderRadius: 8, marginBottom: 16 }}>
            <table className="data-table" style={{ fontSize: 11, marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Applicant</th>
                  <th>Agent</th>
                  <th>Date</th>
                  <th>Carrier</th>
                  <th>Plan</th>
                  <th>Confirmation</th>
                </tr>
              </thead>
              <tbody>
                {exportPairs.map((pair, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace' }}>{pair.csvRow.applicantPhone}</td>
                    <td>{pair.csvRow.applicantFirstName} {pair.csvRow.applicantLastName}</td>
                    <td style={{ fontSize: 10 }}>{pair.csvRow.agentName}</td>
                    <td>{pair.csvRow.date}</td>
                    <td style={{ fontSize: 10 }}>{pair.csvRow.rawFields?.CarrierName || '-'}</td>
                    <td style={{ fontSize: 10 }}>{pair.csvRow.rawFields?.PlanName || '-'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 10 }}>{pair.csvRow.confirmationNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Export result */}
          {exportResult && (
            <div style={{
              padding: '12px 16px', marginBottom: 16, borderRadius: 6,
              background: exportResult.status === 'dry_run' ? '#fff3e0' : exportResult.successCount > 0 ? '#e8f5e9' : '#ffebee',
              color: exportResult.status === 'dry_run' ? '#e65100' : exportResult.successCount > 0 ? '#2e7d32' : '#c62828',
              fontSize: 13,
            }}>
              {exportResult.status === 'dry_run' && (
                <div>
                  <strong>Dry Run Complete</strong> — {exportResult.mappedCount} records mapped, {exportResult.skippedCount} skipped, {exportResult.errorCount} errors.
                  {exportResult.samplePayload && (
                    <details style={{ marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer' }}>Sample payload ({exportResult.samplePayload.length} items)</summary>
                      <pre style={{ fontSize: 10, maxHeight: 400, width: '100%', maxWidth: '100%', overflow: 'auto', boxSizing: 'border-box', background: '#fff', padding: 8, borderRadius: 4, marginTop: 4 }}>
                        {JSON.stringify(exportResult.samplePayload, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
              {exportResult.status === 'complete' && (
                <div>
                  <strong>Export Complete</strong> — {exportResult.successCount} sent successfully
                  {exportResult.failureCount > 0 && `, ${exportResult.failureCount} failed`}
                  {exportResult.skippedCount > 0 && `, ${exportResult.skippedCount} skipped (Glacier)`}.
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
            <button onClick={() => setStep('match')}
              style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
              Back
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleExport(true)} disabled={exporting}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#555' }}>
                {exporting ? 'Processing...' : 'Dry Run'}
              </button>
              <button onClick={() => handleExport(false)} disabled={exporting}
                style={{
                  padding: '8px 20px', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                  background: exporting ? '#ccc' : '#00bcd4', color: '#fff',
                }}>
                {exporting ? 'Exporting...' : `Export ${exportPairs.length} to SMS`}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {activeGroup && (
        <MultiCallModal
          group={activeGroup}
          index={Math.max(1, unresolvedGroups.findIndex((g) => groupKey(g) === activeGroupKey) + 1)}
          total={Math.max(1, unresolvedGroups.length)}
          initialSelected={groupSelections[groupKey(activeGroup)] || []}
          onConfirm={handleGroupConfirm}
          onRemoveEnrollment={handleGroupRemove}
        />
      )}
    </div>
  );
}
